import React from 'react';
import { removeNodeAtPath } from 'react-sortable-tree';

import { CompanySortableTree } from './CompanySortableTree';
import { ProjectSortableTree } from './ProjectSortableTree';
import { cloneDeep, flatten } from 'lodash';

import {
	ContentWrapper,
	ColumnWrapper,
	MiddleContent,
	SidebarPanel,
} from './TaxonomyMapper.styles';
import { CompanyHeader } from './CompanyHeader';
import { ProjectHeader } from './ProjectHeader';
import { CompanyFooter } from './CompanyFooter';
import {
	addNewL1Category,
	expandAll,
	getSortedTree,
	getNormalizedProjectTreeData,
	insertIntoRootNode,
	getNormalizedInsertedTreeNode,
	getTreesChangeData,
	markMappedItems,
	removeMappedItems,
	TREE_MENU_VALUES,
	iterateOverTree,
	getFilteredTree,
	getLeafNodesFromTree,
	getLeafIdsFromNode,
	getNodeKey,
	addNewCategoryToParent,
	addNewCategoryAsSybling,
	mergeTreeNodes,
	getAllSpendFromTree,
	getConfirmationDialogMessage,
} from './utils';
import { getCookie, setCookie } from 'utils/cookies';
import { ConfirmationDialog } from './ConfirmationDialog';
import ApProjectFooter from './ApProjectFooter/ApProjectFooter';

const TAX_MAPPER_DO_NOT_SHOW_CONFIRM_DIALOG =
	'indirect_tax_mapper_do_not_show_dialog';

export class IndirectTaxonomyMapper extends React.Component {
	state = {
		isResetModalOpen: false,
		isProjectResetModalOpen: false,
		confirmationDialogData: null,
	};

	componentDidUpdate() {
		const { needApplyHideToApTree } = this.state;

		let { apTreeData, showMappedItems, onChange } = this.props;

		if (needApplyHideToApTree) {
			if (!showMappedItems) {
				apTreeData = removeMappedItems(apTreeData);
				this.setState({
					needApplyHideToApTree: false,
				});

				onChange({
					apTreeData,
				});
			}
		}
	}

	handleProjectTreeChange = treeData => {
		let stateUpdates = { projectTreeData: treeData };

		if (stateUpdates.projectTreeData) {
			stateUpdates = this.normalizeTreeDataBeforeChange(stateUpdates);
			const { changeData } = stateUpdates;

			const doNotShowDialog = getCookie(TAX_MAPPER_DO_NOT_SHOW_CONFIRM_DIALOG);

			if (!doNotShowDialog && changeData) {
				delete stateUpdates.changeData; // no need to store it in the state

				const confirmationDialogMessage = getConfirmationDialogMessage(
					changeData
				);

				if (confirmationDialogMessage) {
					this.setState({
						confirmationDialogData: {
							pendingStateUpdates: stateUpdates,
							message: confirmationDialogMessage,
						},
					});

					return;
				}
			}

			this.delayedApplyHideToApTree();
		}

		this.props.onChange(stateUpdates);
	};

	delayedApplyHideToApTree = () => {
		// allow drop action to finish
		// delay fixes `Uncaught Error: No node found at the given path.` issue
		setTimeout(() => {
			if (!this.unmounted) {
				this.setState({ needApplyHideToApTree: true });
			}
		}, 1);
	};

	normalizeTreeDataBeforeChange = ({ projectTreeData }) => {
		let { apTreeData, projectTreeData: prevProjectTreeData } = this.props;

		const changeData = getTreesChangeData(prevProjectTreeData, projectTreeData);

		if (!changeData) {
			return { projectTreeData };
		}

		apTreeData = markMappedItems(apTreeData, projectTreeData);

		changeData.parentNodeLevel = changeData.parentNode
			? changeData.parentNode.level
			: 0;

		if (changeData.parentNode) {
			let insertedNode = changeData.parentNode.children[changeData.index];
			changeData.insertedNodeLevel = insertedNode.level;
			changeData.parentNode.children[changeData.index] = { children: [] };

			insertedNode = getNormalizedInsertedTreeNode(
				insertedNode,
				changeData.parentNode
			);

			changeData.parentNode.children[changeData.index] = insertedNode;
			changeData.parentNode.children = flatten(changeData.parentNode.children);
			changeData.insertedNode = insertedNode;
		} else {
			let insertedNode = projectTreeData[changeData.index];
			changeData.insertedNodeLevel = insertedNode.level;
			projectTreeData.splice(changeData.index, 1);

			insertedNode = getNormalizedInsertedTreeNode(
				insertedNode,
				changeData.parentNode
			);

			projectTreeData = insertIntoRootNode(insertedNode, projectTreeData);
			changeData.insertedNode = insertedNode;
		}

		projectTreeData = getNormalizedProjectTreeData(projectTreeData);

		return {
			projectTreeData,
			apTreeData,
			changeData,
		};
	};

	handleApTreeChange = treeData => {
		this.props.onChange({ apTreeData: treeData });
	};

	getPercentageMapped = () => {
		const { apTreeData, projectTreeData } = this.props;

		const totalSpend = apTreeData && apTreeData[0] ? apTreeData[0].value : 0;
		const mappedSpend = getAllSpendFromTree(projectTreeData);

		return Math.round((mappedSpend / totalSpend) * 100) || 0;
	};

	toggleApTreeExpanded = shouldExpand => {
		const { apTreeData } = this.props;

		this.props.onChange({
			apTreeData: expandAll(apTreeData, shouldExpand),
		});
	};

	showApResetConfirmation = () => {
		this.setState({
			isApResetModalOpen: true,
		});
	};

	showProjectResetConfirmation = () => {
		this.setState({
			isApResetModalOpen: false,
			isProjectResetModalOpen: true,
		});
	};

	hideApResetConfirmation = () => {
		this.setState({ isApResetModalOpen: false });
	};

	hideProjectResetConfirmation = () => {
		this.setState({ isProjectResetModalOpen: false });
	};

	// single reset as there is only single source tree - just remove all added items
	handleReset = () => {
		const {
			apTreeData,
			treeData,
			showMappedItems,
			onChange,
			getApTreeDataStateUpdates,
		} = this.props;

		const newProjectTreeData = [];

		let stateUpdates = { projectTreeData: newProjectTreeData };

		stateUpdates = getApTreeDataStateUpdates(stateUpdates, {
			apTreeData,
			treeData,
			showMappedItems,
		});

		onChange(stateUpdates);

		this.hideProjectResetConfirmation();
		this.hideApResetConfirmation();
	};

	handleApTreeSortChange = ({ field, direction }) => {
		let { apTreeData, onChange } = this.props;

		apTreeData = getSortedTree(apTreeData, field, direction);

		onChange({ apTreeData });
	};

	handleApSearch = searchQuery => {
		this.props.onChange({ apSearch: searchQuery });
	};

	toggleProjectTree = shouldExpand => {
		const { projectTreeData } = this.props;

		this.props.onChange({
			projectTreeData: expandAll(projectTreeData, shouldExpand),
		});
	};

	handleProjectTreeSortChange = ({ field, direction }) => {
		let { projectTreeData } = this.props;

		projectTreeData = getSortedTree(projectTreeData, field, direction);

		this.props.onChange({ projectTreeData });
	};

	handleProjectSearch = searchQuery => {
		this.props.onChange({ projectSearch: searchQuery });
	};

	handleAddNewCategory = () => {
		const { projectTreeData, onChange } = this.props;

		let updatedProjectTreeData = addNewL1Category(projectTreeData);

		updatedProjectTreeData = getNormalizedProjectTreeData(
			updatedProjectTreeData
		);

		onChange({ projectTreeData: updatedProjectTreeData });
	};

	handleProjectTreeMenuOptionSelect = (key, node, path, parentNode) => {
		const {
			projectTreeData,
			apTreeData,
			onChange,
			showMappedItems,
			onShowMappedItemsChange,
			getApTreeDataStateUpdates,
		} = this.props;

		if (
			key === TREE_MENU_VALUES.mergeWithAbove ||
			key === TREE_MENU_VALUES.mergeWithBelow
		) {
			const indexInParentNodeChildren = parentNode.children.findIndex(
				item => item.id === node.id
			);
			const mergeNodeIndex =
				key === TREE_MENU_VALUES.mergeWithAbove
					? indexInParentNodeChildren - 1
					: indexInParentNodeChildren + 1;
			const mergeNode = parentNode.children[mergeNodeIndex];
			const mergeResultNode = mergeTreeNodes(node, mergeNode);

			const newProjectTreeData = cloneDeep(projectTreeData);
			iterateOverTree(newProjectTreeData, item => {
				if (item.id === parentNode.id) {
					item.children[mergeNodeIndex] = mergeResultNode;
					// remove target node
					item.children.splice(indexInParentNodeChildren, 1);
				}
			});

			// update projectTreeData
			onChange({ projectTreeData: newProjectTreeData });
		} else if (key === TREE_MENU_VALUES.createChild) {
			let newProjectTreeData = addNewCategoryToParent(projectTreeData, node);

			onChange({ projectTreeData: newProjectTreeData });
		} else if (key === TREE_MENU_VALUES.createSibling) {
			let newProjectTreeData = addNewCategoryAsSybling(projectTreeData, node);

			onChange({ projectTreeData: newProjectTreeData });
		} else if (key === TREE_MENU_VALUES.find) {
			const searchQuery = getLeafIdsFromNode(node);
			// showing mapped items
			if (!showMappedItems) {
				onShowMappedItemsChange();
			}

			setTimeout(() => {
				onChange({
					apSearch: searchQuery,
				});
			}, 1);
		} else if (key === TREE_MENU_VALUES.remove) {
			let newProjectTreeData = removeNodeAtPath({
				treeData: projectTreeData,
				path,
				getNodeKey,
			});

			newProjectTreeData = getNormalizedProjectTreeData(newProjectTreeData);
			let stateUpdates = { projectTreeData: newProjectTreeData };

			const { treeData } = this.props;

			stateUpdates = getApTreeDataStateUpdates(stateUpdates, {
				apTreeData,
				treeData,
				showMappedItems,
			});

			onChange(stateUpdates);
		}
	};

	handleCompanyTreeMenuOptionSelect = (key, node) => {
		if (key === TREE_MENU_VALUES.find) {
			const searchQuery = getLeafIdsFromNode(node);
			this.props.onChange({ projectSearch: searchQuery });
		} else if (key === TREE_MENU_VALUES.remove) {
			const { apTreeData, projectTreeData, onChange } = this.props;

			const stateUpdates = {};
			let newProjectTreeData = [];

			if (node.level === 4) {
				newProjectTreeData = getFilteredTree(
					projectTreeData,
					item => item.id !== node.id
				);
			} else {
				const leafNodeIds = [];
				const trunkIds = [node.id];

				iterateOverTree(node.children, item => {
					if (
						(item.level === 4 || item.level === 5) &&
						item.children.length === 0
					) {
						leafNodeIds.push(item.id);
					} else {
						trunkIds.push(item.id);
					}
				});

				newProjectTreeData = getFilteredTree(
					projectTreeData,
					node =>
						!leafNodeIds.includes(node.id) &&
						!(trunkIds.includes(node.originalId) && node.children.length === 0),
					true // filter parent after filtering its children
				);
			}

			newProjectTreeData = getNormalizedProjectTreeData(newProjectTreeData);
			stateUpdates.projectTreeData = newProjectTreeData;
			stateUpdates.apTreeData = markMappedItems(apTreeData, newProjectTreeData);

			onChange(stateUpdates);
		}
	};

	handleConfirmationDialogConfirm = doNotShowConfirmDialog => {
		const {
			confirmationDialogData: { pendingStateUpdates: stateUpdates },
		} = this.state;
		const { onChange } = this.props;

		this.delayedApplyHideToApTree();

		this.setState({ confirmationDialogData: null });

		onChange(stateUpdates);

		if (doNotShowConfirmDialog) {
			setCookie(TAX_MAPPER_DO_NOT_SHOW_CONFIRM_DIALOG, doNotShowConfirmDialog);
		}
	};

	handleConfirmationDialogClose = () => {
		this.setState({ confirmationDialogData: null });
	};

	render() {
		const {
			confirmationDialogData,
			isApResetModalOpen,
			isProjectResetModalOpen,
		} = this.state;

		const {
			projectTreeData,
			currencySymbol,
			apSearch,
			apTreeData,
			projectSearch,
			canEdit,
		} = this.props;

		return (
			<React.Fragment>
				<ContentWrapper>
					<SidebarPanel>
						<ColumnWrapper>
							<CompanyHeader
								title="AP taxonomy"
								onSortChange={this.handleApTreeSortChange}
								searchOptions={getLeafNodesFromTree(apTreeData)}
								currencySymbol={currencySymbol}
								onSearch={this.handleApSearch}
								previewUrl="/taxonomy-preview-indirect"
							/>
							<CompanySortableTree
								data={apTreeData}
								onChange={this.handleApTreeChange}
								searchQuery={apSearch}
								currencySymbol={currencySymbol}
								canEdit={canEdit}
								onMenuOptionSelect={this.handleCompanyTreeMenuOptionSelect}
								hideTreeItemChart
							/>
							<CompanyFooter
								onExpand={this.toggleApTreeExpanded}
								percentage={this.getPercentageMapped()}
								onReset={this.showApResetConfirmation}
								canEdit={canEdit}
							/>
						</ColumnWrapper>
					</SidebarPanel>
					<MiddleContent>
						<ProjectHeader
							onSortChange={this.handleProjectTreeSortChange}
							searchOptions={getLeafNodesFromTree(projectTreeData)}
							currencySymbol={currencySymbol}
							onSearch={this.handleProjectSearch}
							onAddNew={this.handleAddNewCategory}
							canEdit={canEdit}
						/>

						<ProjectSortableTree
							data={projectTreeData}
							onChange={this.handleProjectTreeChange}
							searchQuery={projectSearch}
							currencySymbol={currencySymbol}
							onMenuOptionSelect={this.handleProjectTreeMenuOptionSelect}
							hideTreeItemChart
							canEdit={canEdit}
						/>

						<ApProjectFooter
							onExpand={this.toggleProjectTree}
							onReset={this.showProjectResetConfirmation}
							canEdit={canEdit}
						/>
					</MiddleContent>
				</ContentWrapper>
				{confirmationDialogData && (
					<ConfirmationDialog
						text={confirmationDialogData.message}
						onConfirm={this.handleConfirmationDialogConfirm}
						onClose={this.handleConfirmationDialogClose}
					/>
				)}

				{isApResetModalOpen && (
					<ConfirmationDialog
						text="Please note, AP taxonomy mappings will be fully removed from the project taxonomy."
						title="AlixPartners taxonomy reset"
						suppressCheckbox
						onConfirm={this.handleReset}
						onClose={this.hideApResetConfirmation}
					/>
				)}

				{isProjectResetModalOpen && (
					<ConfirmationDialog
						text="Please note, Project Taxonomy will be deleted"
						title="Project taxonomy reset"
						suppressCheckbox
						onConfirm={this.handleReset}
						onClose={this.hideProjectResetConfirmation}
					/>
				)}
			</React.Fragment>
		);
	}
}

export default IndirectTaxonomyMapper;
